import React, { useRef, useEffect, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import "./hotelInventory.css";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");
const toStr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const parse = (v) => {
  const s = (v || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split("-").map(Number);
  return { y, m: m - 1, d };
};

export const nextDay = (s) => {
  const p = parse(s);
  if (!p) return "";
  const dt = new Date(Date.UTC(p.y, p.m, p.d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

const DatePicker = ({ value, onChange, placeholder, min }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const parsed = parse(value);

  const today = new Date();
  const todayStr = toStr(today.getFullYear(), today.getMonth(), today.getDate());
  const floorStr = min && min > todayStr ? min : todayStr;
  const fp = parse(floorStr) || { y: today.getFullYear(), m: today.getMonth() };

  const [view, setView] = useState(() => {
    const p = parse(value) || parse(min);
    const base = p ? new Date(p.y, p.m, 1) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  useEffect(() => {
    if (!open) return undefined;
    const p = parse(value) || parse(min);
    if (p) setView(new Date(p.y, p.m, 1));
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const y = view.getFullYear();
  const m = view.getMonth();
  const lead = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = 1; d <= days; d += 1) cells.push(d);

  const isToday = (d) => d && y === today.getFullYear() && m === today.getMonth() && d === today.getDate();
  const isSel = (d) => d && parsed && parsed.y === y && parsed.m === m && parsed.d === d;
  const isDisabled = (d) => d && toStr(y, m, d) < floorStr;

  const floorMonth = new Date(fp.y, fp.m, 1);
  const clampView = (dt) => (dt < floorMonth ? new Date(floorMonth) : dt);
  const atMin = y < fp.y || (y === fp.y && m <= fp.m);

  const years = [];
  for (let yy = Math.min(fp.y, y); yy <= today.getFullYear() + 3; yy += 1) years.push(yy);

  const pick = (d) => { if (isDisabled(d)) return; onChange(toStr(y, m, d)); setOpen(false); };
  const shift = (delta) => setView(clampView(new Date(y, m + delta, 1)));
  const setMonthSel = (mm) => setView(clampView(new Date(y, mm, 1)));
  const setYearSel = (yy) => setView(clampView(new Date(yy, m, 1)));
  const pickToday = () => {
    const target = todayStr >= floorStr ? todayStr : floorStr;
    const p = parse(target);
    setView(new Date(p.y, p.m, 1));
    onChange(target);
    setOpen(false);
  };

  const label = parsed ? `${pad(parsed.d)} ${MON[parsed.m]} ${parsed.y}` : "";

  return (
    <div className="hi-dp" ref={wrapRef}>
      <button type="button" className={`hi-dp-trigger${open ? " is-open" : ""}`} onClick={() => setOpen((o) => !o)}>
        <span className={label ? "hi-dp-val" : "hi-dp-ph"}>{label || placeholder || "Select date"}</span>
        <Calendar size={16} />
      </button>
      {open && (
        <div className="hi-dp-pop">
          <div className="hi-dp-head">
            <button type="button" className="hi-dp-nav" disabled={atMin} onClick={() => shift(-1)}><ChevronLeft size={16} /></button>
            <div className="hi-dp-sel">
              <select value={m} onChange={(e) => setMonthSel(Number(e.target.value))}>
                {MON.map((name, i) => <option key={i} value={i} disabled={y === fp.y && i < fp.m}>{name}</option>)}
              </select>
              <select value={y} onChange={(e) => setYearSel(Number(e.target.value))}>
                {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
              </select>
            </div>
            <button type="button" className="hi-dp-nav" onClick={() => shift(1)}><ChevronRight size={16} /></button>
          </div>
          <div className="hi-dp-grid">
            {DOW.map((d) => <span key={d} className="hi-dp-dow">{d}</span>)}
            {cells.map((d, i) => (d === null
              ? <span key={`b${i}`} className="hi-dp-blank" />
              : <button type="button" key={d} disabled={isDisabled(d)} className={`hi-dp-cell${isSel(d) ? " is-sel" : ""}${isToday(d) ? " is-today" : ""}`} onClick={() => pick(d)}>{d}</button>
            ))}
          </div>
          <div className="hi-dp-foot">
            <button type="button" className="hi-dp-clear" onClick={() => { onChange(""); setOpen(false); }}>Clear</button>
            <button type="button" className="hi-dp-today" onClick={pickToday}>Today</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
